import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { truckingBillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-PH', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '—';

const fmtLong = (dateStr) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });

function exportCSV(bills) {
  const headers = ['#', 'Bill No.', 'Bill Date', 'Due Date', 'Trucking Name', 'Is Received', 'Carrier', 'Total Amount'];
  const rows = bills.map((b, i) => [
    i + 1,
    b.BillName || '',
    fmtDate(b.BillDate),
    fmtDate(b.BillDueDate),
    b.TruckingId?.CompanyName || b.TruckingName || '',
    b.IsReceived ? 'Yes' : 'No',
    b.CarrierName || '',
    b.totalAmount || 0,
  ]);
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `daily-bill-report-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DailyBillReportPage() {
  const navigate = useNavigate();
  const todayStr = new Date().toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [billType, setBillType] = useState('');
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const loadReport = async (from = dateFrom, to = dateTo, type = billType) => {
    setLoading(true);
    try {
      const res = await truckingBillAPI.getDailyBill({ dateFrom: from, dateTo: to, billType: type });
      const d = res.data || {};
      setBills(Array.isArray(d.data) ? d.data : []);
      setSummary(d.summary || {});
      setLoaded(true);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, []); // eslint-disable-line

  const handlePrint = () => {
    const params = new URLSearchParams({ dateFrom, dateTo, billType });
    window.open(`/billing/reports/print/daily-bill?${params}`, '_blank');
  };

  const dateLabel = dateFrom === dateTo
    ? fmtLong(dateFrom)
    : `${fmtLong(dateFrom)} – ${fmtLong(dateTo)}`;

  const totalSales = summary.totalSales || 0;
  const totalReceived = summary.totalReceived || 0;
  const totalUnpaid = summary.totalUnpaid || 0;

  return (
    <Layout pageTitle="Daily Billing Report">
      <div className="space-y-4 max-w-6xl">
        {/* Filter bar */}
        <div className="flex flex-wrap items-end gap-4 bg-white rounded-lg shadow px-4 py-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date From</label>
            <input type="date" value={dateFrom} max={dateTo}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date To</label>
            <input type="date" value={dateTo} min={dateFrom}
              onChange={(e) => setDateTo(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Bill Type</label>
            <select value={billType} onChange={(e) => setBillType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All</option>
              <option value="1">Freight Forwarding</option>
              <option value="2">Trucking</option>
            </select>
          </div>
          <button onClick={() => loadReport()} disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
            {loading ? 'Loading…' : 'Load Report'}
          </button>
          {loaded && bills.length > 0 && (
            <>
              <button onClick={handlePrint}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                Print
              </button>
              <button onClick={() => exportCSV(bills)}
                className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium hover:bg-green-100">
                Export CSV
              </button>
            </>
          )}
        </div>

        {/* Summary cards */}
        {loaded && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow px-4 py-3">
              <p className="text-xs text-gray-500">Total Sales</p>
              <p className="text-xl font-semibold text-gray-800">₱{PHP(totalSales)}</p>
              <p className="text-xs text-gray-400">{bills.length} bills · {dateLabel}</p>
            </div>
            <div className="bg-white rounded-lg shadow px-4 py-3">
              <p className="text-xs text-gray-500">Received</p>
              <p className="text-xl font-semibold text-green-600">₱{PHP(totalReceived)}</p>
              <p className="text-xs text-gray-400">{bills.filter((b) => b.IsReceived).length} bills marked received</p>
            </div>
            <div className="bg-white rounded-lg shadow px-4 py-3">
              <p className="text-xs text-gray-500">Unpaid</p>
              <p className="text-xl font-semibold text-rose-600">₱{PHP(totalUnpaid)}</p>
              <p className="text-xs text-gray-400">{bills.filter((b) => !b.IsReceived).length} bills unpaid</p>
            </div>
          </div>
        )}

        {/* Table */}
        {loaded && (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Bill No.', 'Bill Date', 'Due Date', 'Trucking Name', 'Carrier', 'Received', 'Total Amount'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bills.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No bills found.</td></tr>
                ) : bills.map((b, i) => {
                  const truckingName = b.TruckingId?.CompanyName || b.TruckingName || '—';
                  return (
                    <tr key={b._id} onClick={() => navigate(`/billing/trucking-billing/${b._id}/edit`)}
                      className="hover:bg-teal-50 cursor-pointer">
                      <td className="px-4 py-2 text-gray-500">{i + 1}</td>
                      <td className="px-4 py-2 font-medium text-teal-700">{b.BillName}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(b.BillDate)}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDate(b.BillDueDate)}</td>
                      <td className="px-4 py-2">{truckingName}</td>
                      <td className="px-4 py-2">{b.CarrierName || '—'}</td>
                      <td className="px-4 py-2">
                        {b.IsReceived
                          ? <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">Received</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Unpaid</span>}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">₱{PHP(b.totalAmount)}</td>
                    </tr>
                  );
                })}
                {bills.length > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={7} className="px-4 py-2 text-right text-gray-600">Grand Total</td>
                    <td className="px-4 py-2 text-right text-gray-800">₱{PHP(totalSales)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
